import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { api } from '../../utils/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import { AvatarHelper } from '../../utils/avatarHelper'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  TrashIcon,
  UserCircleIcon,
  StarIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'

interface Teacher {
  id: string
  title: string
  introduction: string
  specialties: string[]
  rating: number
  status: 'active' | 'pending' | 'rejected' | 'more_info_required'
  bio: string
  experience: string
  education: string
  phoneNumber?: string
  highestDegree?: string
  graduatedUniversity?: string
  graduationYear?: string
  major?: string
  teachingExperience?: string
  createdAt: string
  updatedAt?: string
  user: {
    id?: string
    firstName: string
    lastName: string
    avatar?: string
    email: string
    phone?: string
  }
  _count?: {
    courses: number
  }
}

const AdminTeachers = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'rejected' | 'more_info_required'>('all')
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingTeacher, setDeletingTeacher] = useState<Teacher | null>(null)

  const fetchTeachers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      // 获取所有教师（包括各种状态）
      const response = await fetch('/api/teachers', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setTeachers(Array.isArray(data) ? data : [])
        setFilteredTeachers(Array.isArray(data) ? data : [])
      } else {
        console.error('获取教师列表失败')
        setTeachers([])
        setFilteredTeachers([])
      }
    } catch (error) {
      console.error('获取教师列表失败:', error)
      toast.error('获取教师列表失败')
      setTeachers([])
      setFilteredTeachers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { 
    fetchTeachers() 
  }, [])

  useEffect(() => {
    let filtered = [...teachers]
    
    // 搜索过滤
    if (searchTerm) {
      filtered = filtered.filter(teacher => 
        teacher.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.specialties?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }
    
    // 状态过滤
    if (statusFilter !== 'all') {
      filtered = filtered.filter(teacher => teacher.status === statusFilter)
    }
    
    setFilteredTeachers(filtered)
  }, [teachers, searchTerm, statusFilter])

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      more_info_required: 'bg-blue-100 text-blue-800'
    }
    const labels = { 
      active: '已认证', 
      pending: '待审核', 
      rejected: '已拒绝',
      more_info_required: '需补充'
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    )
  }

  const renderStars = (rating: number) => {
    const stars: React.ReactNode[] = []
    for (let i = 0; i < 5; i++) {
      stars.push(
        i < Math.floor(rating) ? (
          <StarSolidIcon key={i} className="h-4 w-4 text-yellow-400" />
        ) : (
          <StarIcon key={i} className="h-4 w-4 text-gray-300" />
        )
      )
    }
    return stars
  }

  if (loading) {
    return <div className="flex justify-center h-96 items-center"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 relative">
      {/* 头部 */}
      <div className="md:flex md:items-center md:justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">教师管理</h1>
          <p className="mt-1 text-sm text-gray-500">
            共 {filteredTeachers.length} 位教师
          </p>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <Link
            to="/admin/teacher-applications"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm text-sm font-medium transition-all"
          >
            <AcademicCapIcon className="h-4 w-4 mr-2" />
            教师申请审核
          </Link>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索姓名、邮箱、职称、专长..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500"
            >
              <option value="all">所有状态</option>
              <option value="active">已认证</option>
              <option value="pending">待审核</option>
              <option value="more_info_required">需补充</option>
              <option value="rejected">已拒绝</option>
            </select>
          </div>
        </div>
      </div>

      {/* 教师列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTeachers.map(teacher => (
          <div 
            key={teacher.id} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setSelectedTeacher(teacher)}
          >
            <div className="p-6">
              {/* 头像和基本信息 */}
              <div className="flex items-center mb-4">
                <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {teacher.user.avatar ? (
                    <img 
                      src={AvatarHelper.getAvatarUrl(teacher.user.avatar) || undefined} 
                      alt="" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xl font-bold text-indigo-600">
                      {teacher.user.lastName?.[0] || teacher.user.firstName?.[0] || '?'}
                    </span>
                  )}
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {teacher.user.lastName}{teacher.user.firstName}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">{teacher.title}</p>
                </div>
                {getStatusBadge(teacher.status)}
              </div>

              {/* 评分 */}
              <div className="flex items-center mb-3">
                <div className="flex items-center">
                  {renderStars(Number(teacher.rating) || 0)}
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  {(Number(teacher.rating) || 0).toFixed(1)} ({teacher._count?.courses || 0} 门课程)
                </span>
              </div>

              {/* 简介 */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {teacher.introduction || '暂无简介'}
              </p>

              {/* 专长标签 */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(teacher.specialties || []).slice(0, 3).map((specialty, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {specialty}
                  </span>
                ))}
                {(teacher.specialties?.length || 0) > 3 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    +{(teacher.specialties?.length || 0) - 3}
                  </span>
                )}
              </div>

              {/* 底部信息 */}
              <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                <div className="flex items-center">
                  <BookOpenIcon className="h-4 w-4 mr-1" />
                  {teacher._count?.courses || 0} 门课程
                </div>
                <div className="flex items-center text-indigo-600">
                  查看详情
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTeachers.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <UserCircleIcon className="mx-auto h-16 w-16 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">暂无教师</h3>
          <p className="mt-2 text-gray-500">
            {searchTerm || statusFilter !== 'all' ? '没有找到符合条件的教师' : '目前还没有教师入驻'}
          </p>
        </div>
      )}

      {/* 教师详情抽屉 */}
      {selectedTeacher && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 z-40 transition-opacity" 
            onClick={() => setSelectedTeacher(null)}
          />
          <div className="fixed inset-y-0 right-0 w-full md:w-[560px] bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900">教师详情</h2>
                <button 
                  onClick={() => setSelectedTeacher(null)}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* 教师头像和基本信息 */}
              <div className="text-center mb-8 pb-6 border-b border-gray-200">
                <div className="w-24 h-24 rounded-full bg-indigo-100 mx-auto mb-4 flex items-center justify-center overflow-hidden">
                  {selectedTeacher.user.avatar ? (
                    <img 
                      src={AvatarHelper.getAvatarUrl(selectedTeacher.user.avatar) || undefined} 
                      alt="" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-indigo-600">
                      {selectedTeacher.user.lastName?.[0] || selectedTeacher.user.firstName?.[0] || '?'}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedTeacher.user.lastName}{selectedTeacher.user.firstName}
                </h3>
                <p className="text-gray-500">{selectedTeacher.title}</p>
                <div className="mt-2">
                  {getStatusBadge(selectedTeacher.status)}
                </div>
                <div className="flex items-center justify-center mt-3">
                  {renderStars(Number(selectedTeacher.rating) || 0)}
                  <span className="ml-2 text-sm text-gray-600">
                    {(Number(selectedTeacher.rating) || 0).toFixed(1)}
                  </span>
                </div>
              </div>

              {/* 详细信息 */}
              <div className="space-y-6">
                {/* 联系信息 */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">联系信息</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">邮箱</span>
                      <span className="text-gray-900">{selectedTeacher.user.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">电话</span>
                      <span className="text-gray-900">{selectedTeacher.phoneNumber || selectedTeacher.user.phone || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* 教育背景 */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">教育背景</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">最高学历</span>
                      <span className="text-gray-900">{selectedTeacher.highestDegree || selectedTeacher.education || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">毕业院校</span>
                      <span className="text-gray-900">{selectedTeacher.graduatedUniversity || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">专业</span>
                      <span className="text-gray-900">{selectedTeacher.major || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">毕业年份</span>
                      <span className="text-gray-900">{selectedTeacher.graduationYear || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* 个人简介 */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">个人简介</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {selectedTeacher.introduction || selectedTeacher.bio || '暂无简介'}
                  </p>
                </div>

                {/* 教学经验 */}
                {(selectedTeacher.teachingExperience || selectedTeacher.experience) && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">教学经验</h4>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedTeacher.teachingExperience || selectedTeacher.experience}
                    </p>
                  </div>
                )}

                {/* 专业领域 */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">专业领域</h4>
                  <div className="flex flex-wrap gap-2">
                    {(selectedTeacher.specialties || []).map((specialty, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {specialty}
                      </span>
                    ))}
                    {(!selectedTeacher.specialties || selectedTeacher.specialties.length === 0) && (
                      <span className="text-sm text-gray-500">暂无专业标签</span>
                    )}
                  </div>
                </div>

                {/* 操作按钮 */}
                <div className="flex gap-3 pt-4">
                  <Link
                    to={`/teachers/${selectedTeacher.id}/profile`}
                    className="flex-1 text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                  >
                    查看完整资料
                  </Link>
                  {selectedTeacher.status === 'pending' && (
                    <Link
                      to="/admin/teacher-applications"
                      className="flex-1 text-center px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 font-medium"
                    >
                      去审核
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default AdminTeachers
